<template>
  <view-page
    :title="pageTitle"
    :loading="loading"
    :delete-dialog-title="$t('DeleteComment')"
    :delete-dialog-content="deleteMessage"
    :delete-button="userCanDeleteComment"
    @deleteItem="deleteComment"
    :edit-button="userCanDeleteComment"
    @editItem="editComment"
    :permission-name="permissionName"
  >
    <template v-slot:leftSide>
      <view-page-data-field
        v-for="field in fields"
        :key="field.template"
        :icon="field.icon"
        :label="field.label"
        :data="field.data"
      />
    </template>

    <template v-slot:headerRightSide>
      <div>
        <management-tabs-and-search
          :tabs="tabs"
          :open-tab="openTab"
          @update-open-tab="openTab = $event"
        />
        <management-add-button
          v-if="openTab === 'attachments'"
          :label="$t('AddAttachment')"
          @buttonClick="openAttachmentForm"
          :disabled="false"
          :list-missing-permissions="[]"
        />
      </div>
    </template>

    <template v-slot:rightSide>
      <v-tabs-items :value="openTab" v-if="comment">
        <v-tab-item value="description" class="elevation-2">
          <textarea
            v-model="comment.text"
            id="description"
            class="input-field"
            readonly
            style="height: 382px"
          />
        </v-tab-item>
        <v-tab-item value="attachments">
          <attachments-table
            :attachments="comment.attachments"
            @open-attachment="openAttachment($event)"
            @reload-comment="getComment"
          />
        </v-tab-item>
      </v-tabs-items>
      <attachment-form
        :open="attachmentForm.isOpen"
        @add-attachment="saveAttachment($event)"
        @close="closeAttachmentForm"
      />
    </template>
  </view-page>
</template>

<script>
import { mapActions } from "vuex";
import ViewPage from "@/components/Management/ViewPage/ViewPage";
import ViewPageDataField from "@/components/Management/ViewPage/ViewPageDataField";
import ManagementTabsAndSearch from "@/components/_Organisms/ManagementTabsAndSearch";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import dateformat from "dateformat";
import AttachmentsTable from "@/components/Support/TechnicalSupport/AttachmentsTable";
import AttachmentForm from "@/components/Support/TechnicalSupport/AttachmentForm";
import ManagementAddButton from "@/components/Management/ManagementAddButton";

export default {
  name: "TicketCommentDetails",

  components: {
    ViewPageDataField,
    ManagementTabsAndSearch,
    ViewPage,
    AttachmentsTable,
    AttachmentForm,
    ManagementAddButton
  },

  mixins: [managementDatatableMixin, checkPermissionsMixin],

  data() {
    return {
      comment: null,
      loading: false,
      openTab: "description",
      fields: null,

      permissionName: "third_party_apis_youtrack_comments",

      attachmentForm: {
        isOpen: false
      }
    };
  },

  computed: {
    commentID() {
      return this.$route.params.commentId;
    },

    ticketID() {
      return this.$route.params.id;
    },

    tabs() {
      return [
        {
          value: "description",
          text: this.$i18n.t("Description"),
          hasSearch: false
        },
        {
          value: "attachments",
          text: this.$i18n.tc("Attachment", 2),
          hasSearch: false
        }
      ];
    },

    deleteMessage() {
      return this.comment
        ? this.$i18n.t("DeleteConfirmation") + " " + this.commentID + "?"
        : "";
    },

    userCanDeleteComment() {
      return this.comment
        ? this.userHasPermission("third_party_apis_youtrack_issues_destroy") &&
            this.userIsTheAuthor(this.comment.author.id)
        : this.userHasPermission("third_party_apis_youtrack_issues_destroy");
    },

    userCanEditComment() {
      return this.comment
        ? this.userHasPermission("third_party_apis_youtrack_issues_update") &&
            this.userIsTheAuthor(this.comment.author.id)
        : this.userHasPermission("third_party_apis_youtrack_issues_update");
    },

    pageTitle() {
      return this.$i18n.tc("Comment", 1) + " " + this.commentID;
    }
  },

  watch: {
    commentID() {
      this.getComment();
    }
  },

  mounted() {
    this.getComment();
  },

  methods: {
    ...mapActions([
      "fetchCommentByID",
      "deleteCommentByID",
      "createCommentAttachment"
    ]),

    async getComment() {
      if (this.commentID && this.ticketID) {
        this.loading = true;
        try {
          this.comment = await this.fetchCommentByID({
            commentID: this.commentID,
            ticketID: this.ticketID
          });

          if (this.comment.error === "Not Found") {
            this.$router.push("/error");
          }
        } catch (e) {
          console.error(e);
        } finally {
          this.loading = false;
          this.fields = this.getFields();
        }
      }
    },

    async deleteComment() {
      try {
        this.loading = true;
        await this.deleteCommentByID({
          commentID: this.commentID,
          ticketID: this.ticketID
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.$router.back();
      }
    },

    getFields() {
      if (this.comment) {
        return [
          {
            icon: "person",
            label: this.$i18n.t("CreatedBy"),
            data: this.comment.author.name
          },
          {
            icon: "event",
            label: this.$i18n.t("CreationDate"),
            data: this.comment.created
          },
          {
            icon: "update",
            label: this.$i18n.t("Updated"),
            data: this.comment.updated
          }
        ];
      }
      return null;
    },

    formatDates() {
      this.comment.created = this.formatDate(this.comment.created);
      this.comment.updated = this.formatDate(this.comment.updated);
    },

    formatDate(date) {
      if (date) {
        return dateformat(new Date(date), "UTC:yyyy-mm-dd HH:MM:ss");
      }
      return null;
    },

    userIsTheAuthor(userID) {
      return userID === this.$store.getters.currentUserID ? true : false;
    },

    editComment() {
      if (this.userHasPermission("third_party_apis_youtrack_issues_update")) {
        this.$router.push({
          name: "comments-edit",
          params: {
            id: this.ticketID,
            commentId: this.commentID
          }
        });
      }
    },

    async saveAttachment(newAttachment) {
      this.loading = true;
      let upload = [];
      let formData = new FormData();

      if (newAttachment instanceof File) {
        upload.push(newAttachment);
      } else {
        upload = Array.from(newAttachment);
      }

      for (let i = 0; i < upload.length; i++) {
        formData.append("upload[]", upload[i]);
      }

      try {
        this.comment = await this.createCommentAttachment({
          formData,
          ticketID: this.ticketID,
          commentID: this.commentID
        });
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
        this.getComment();
      }
    },

    openAttachment(attachment) {
      window.open(attachment.url);
    },

    openAttachmentForm() {
      this.attachmentForm.isOpen = true;
    },

    closeAttachmentForm() {
      this.attachmentForm.isOpen = false;
    }
  }
};
</script>

<style scoped>
#description {
  border: transparent;
  padding: 20px;
  cursor: inherit;
  height: 422px;
}
</style>



// WEBPACK FOOTER //
// src/components/Support/TechnicalSupport/TicketCommentDetails.vue